// components/lists/EnrolmentList.js
import React, { useState, useEffect, useMemo } from "react";
import { getAllEnrollments } from "../../services/EnrolmentService";
import { Filter } from "lucide-react";

function EnrolmentList({ onSelectStudent, onPendingCountChange }) {
  const [activeCollege, setActiveCollege] = useState("All");
  const [activeStatus, setActiveStatus] = useState("pending"); // default view
  const [enrolments, setEnrolments] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- helpers ---
  const normalizeStatus = (raw) => {
    if (!raw) return "pending";
    const t = String(raw).toLowerCase().trim();
    if (t.includes("accept")) return "accepted";
    if (t.includes("pend")) return "pending";
    return "pending";
  };

  const getStatusFromRecord = (e) => {
    const rawStatus =
      e.status ??
      e.enrolmentStatus ??
      e.formState?.status ??
      e.formState?.meta?.status;

    let normalized = normalizeStatus(rawStatus);

    // Migration logic: accepted & migratedToStudentSystem -> migrated
    if (
      normalized === "accepted" &&
      (e.migratedToStudentSystem === true ||
        e.formState?.meta?.migratedToStudentSystem === true)
    ) {
      normalized = "migrated";
    }

    return normalized;
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await getAllEnrollments();
        if (!alive) return;
        setEnrolments(data);
      } catch (err) {
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // derive counts & filtered data (memoized)
  const statusCounts = useMemo(() => {
    const counts = { pending: 0, accepted: 0, migrated: 0, deleted: 0 };
    for (const e of enrolments) {
      const s = getStatusFromRecord(e);
      counts[s] = (counts[s] ?? 0) + 1;
    }
    return counts;
  }, [enrolments]);

  const filteredEnrolments = useMemo(() => {
    return enrolments.filter((e) => {
      const matchCollege =
        activeCollege === "All" || e.formState?.personal?.college === activeCollege;
      const s = getStatusFromRecord(e);
      const matchStatus = activeStatus === "all" ? true : s === activeStatus;
      return matchCollege && matchStatus;
    });
  }, [enrolments, activeCollege, activeStatus]);

  // lift pending count to parent (for sidebar bubble)
  useEffect(() => {
    if (onPendingCountChange) {
      onPendingCountChange(statusCounts.pending || 0);
    }
  }, [statusCounts.pending, onPendingCountChange]);

  // --- UI styles (no Tailwind) ---
  const barWrap = {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 16,
  };
  const chip = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "6px 10px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  };
  const labelStyle = { fontSize: 13, color: "#6b7280", fontWeight: 600 };
  const selectStyle = {
    border: "none",
    background: "transparent",
    fontSize: 14,
    outline: "none",
    padding: "4px 6px",
    cursor: "pointer",
  };

  return (
    <div className="record-list">
      {/* Right-aligned filter chip */}
      <div style={barWrap}>
        <div style={chip}>
          <Filter size={16} color="#6b7280" />
          <span style={labelStyle}>Status</span>
          <select
            value={activeStatus}
            onChange={(e) => setActiveStatus(e.target.value)}
            style={selectStyle}
            aria-label="Filter enrolments by status"
          >
            <option value="all">All ({enrolments.length})</option>
            <option value="pending">New ({statusCounts.pending || 0})</option>
            <option value="accepted">Accepted ({statusCounts.accepted || 0})</option>
            <option value="migrated">Migrated ({statusCounts.migrated || 0})</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p>Loading enrolments...</p>
      ) : (
        <table
          className="record-table"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead>
            <tr style={{ borderBottom: "2px solid #ddd" }}>
              <th style={{ padding: "8px", textAlign: "center" }}>Enrolment ID</th>
              <th style={{ padding: "8px", textAlign: "center" }}>Student Name</th>
              <th style={{ padding: "8px", textAlign: "center" }}>Campus</th>
              <th style={{ padding: "8px", textAlign: "center" }}>Agent</th>
            </tr>
          </thead>
          <tbody>
            {filteredEnrolments.map((enrolment) => {
              const personal = enrolment.formState?.personal || {};
              return (
                <tr
                  key={enrolment.id}
                  onClick={() => onSelectStudent(enrolment)}
                  style={{ cursor: "pointer", borderBottom: "1px solid #eee" }}
                >
                  <td style={{ padding: "8px", textAlign: "center" }}>
                    {enrolment.enrolmentReferenceId || "-"}
                  </td>
                  <td style={{ padding: "8px", textAlign: "center" }}>
                    {personal.givenNames} {personal.familyName}
                  </td>
                  <td style={{ padding: "8px", textAlign: "center" }}>
                    {enrolment.formState?.campusCourse?.campus}
                  </td>
                  <td style={{ padding: "8px", textAlign: "center" }}>
                    {personal.agent?.agencyName || personal.agencyName || "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default EnrolmentList;
